import { jsx, type JsxElementProps } from '@amazon/vinyl-tsx'
import { navigateTo } from '@/router/router'
import { getAuth } from '@/auth/authRef'
import { windowEvents } from '@/util/interaction'
import { bullfrogVersion } from '@/version'
import { getDeviceConfig } from '@/device/deviceConfig'
import { debug } from '@/core/debug'
import { updateQueryParam } from '@/util/domUtil'
import { createDisposer } from '@amazon/vinyl-util'
import { type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import { getString } from '@/header/i18n/bundle.generated'
import { Icon } from '@/components/Icon'
import { getUserInfo } from '@/customer/service/userInfoService'

export type HeaderMenuProps = JsxElementProps<'div'> & {
    visible: MutableValue<boolean>
}

// Hornet's tier label is the short marketing name for the user's
// current plan. Anything we don't recognise (or null while user-info
// is still loading) hides the label entirely so we don't flash a
// blank header.
function tierLabel$(): ObservableValue<string | null> {
    return getUserInfo().map((info) => {
        const tier = info?.user?.tier
        if (tier === 'UNLIMITED') return getString('dmusic_user_tier_amazon_music_unlimited').value
        if (tier === 'PRIME') return getString('dmusic_user_tier_amazon_music_prime').value
        if (tier === 'FREE') return getString('dmusic_user_tier_free_streaming').value
        return null
    })
}

function openExternal(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer')
}

export function HeaderMenu(props: HeaderMenuProps) {
    const auth = getAuth()

    const hideSelf = () => (props.visible.value = false)

    const tier$ = tierLabel$()
    const hasTier$ = tier$.map((t) => t != null)

    type IconName = Parameters<typeof Icon>[0]['icon']
    const navItem = (
        icon: IconName,
        label: ObservableValue<string> | string,
        onclick: () => void
    ) => (
        <li
            className="headerMenuItem"
            tabIndex={0}
            onclick={() => {
                onclick()
                hideSelf()
            }}
        >
            <Icon icon={icon} className="headerMenuItemIcon" />
            <span className="headerMenuItemLabel">{label}</span>
        </li>
    )

    return (
        <div
            {...props}
            className={`headerMenu ${props.className ?? ''}`}
            onConnect={(view) => {
                const { add, dispose } = createDisposer()

                // Anchor the menu beneath the profile-avatar button:
                // right edge flush with the avatar's right edge, top
                // just below the header. The avatar moves with the
                // header's max-content-width, so a fixed `right`
                // offset doesn't track it on wide viewports.
                const reposition = () => {
                    const avatar = document.querySelector<HTMLElement>('.profileAvatar')
                    if (!avatar) return
                    const r = avatar.getBoundingClientRect()
                    view.style.right = `${Math.max(4, window.innerWidth - r.right)}px`
                    view.style.top = `${r.bottom + 8}px`
                }

                add(
                    props.visible.onData((vis) => {
                        if (vis) reposition()
                    })
                )
                window.addEventListener('resize', reposition)
                add(() => window.removeEventListener('resize', reposition))

                add(
                    windowEvents.on('click', (event) => {
                        const target = event.target as Node
                        if (view.contains(target)) return
                        hideSelf()
                    })
                )

                return dispose
            }}
        >
            {/* Tier header — accent-coloured marketing badge for the
                user's current plan. Hidden while user-info hasn't
                loaded so we don't flash a blank pill. */}
            <div className="headerMenuTier" visible={hasTier$}>
                {tier$.map((t) => t ?? '')}
            </div>
            <ul>
                {navItem(
                    'profileFilled',
                    getString('dmusic_settings_overflow_my_profile_text'),
                    () => navigateTo('profile')
                )}
                {navItem(
                    'thumbUp',
                    getString('dmusic_identity_profiles_setup_profile_success_atc_tab_title'),
                    () => navigateTo('tastes')
                )}
                {
                    // Streaming-quality settings are not configurable
                    // for native HLS, so hide the row in that case.
                    getDeviceConfig().hls
                        ? null
                        : navItem(
                              'eq',
                              getString('dmusic_settings_overflow_audio_quality_text'),
                              () => navigateTo('streamingQualitySettings')
                          )
                }
                {navItem('settings', getString('dmusic_amazon_music_settings'), () =>
                    openExternal(
                        'https://www.amazon.com/music/settings?ref=dp_amp_settings_yasettings_click'
                    )
                )}
                {navItem('help', getString('dmusic_get_help'), () => navigateTo('help'))}
                {navItem('information', getString('dmusic_terms_and_conditions'), () =>
                    openExternal(
                        'https://www.amazon.com/gp/help/customer/display.html?pop-up=1&nodeId=201380010'
                    )
                )}
                <li
                    className="headerMenuItem"
                    tabIndex={0}
                    onclick={() => {
                        updateQueryParam('debug', debug ? null : 'true')
                    }}
                >
                    <Icon icon="information" className="headerMenuItemIcon" />
                    <span className="headerMenuItemLabel">
                        {debug ? 'Disable' : 'Enable'} Debug Logging
                    </span>
                </li>
                <li className="headerMenuDivider" />
                <li
                    className="headerMenuItem destructive"
                    tabIndex={0}
                    onclick={() => {
                        auth.signOut()
                        hideSelf()
                    }}
                >
                    <Icon icon="exclamationFilled" className="headerMenuItemIcon" />
                    <span className="headerMenuItemLabel">
                        {getString('dmusic_settings_overflow_sign_out_text')}
                    </span>
                </li>
                <li className="headerMenuVersion">Version {bullfrogVersion.str}</li>
            </ul>
        </div>
    )
}
